import { useEffect, useState } from 'react';
import { auth } from '@/firebase';
import { getAdminWhoami } from '@/api/admin';
import { useAuth } from './AuthContext';

/**
 * Client-side probe for super-admin console access.
 *
 * Uses the same gate as `/admin/console/*`: custom claim + allowlist + MFA
 * (via `GET /admin/console/whoami`). A stale `superAdmin` claim or missing MFA
 * must not surface the settings link.
 */
export function useIsSuperAdmin(): { isSuperAdmin: boolean; loading: boolean } {
  const { user, loading: authLoading } = useAuth();
  const [checking, setChecking] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!user || !auth.currentUser) {
        if (!cancelled) {
          setIsSuperAdmin(false);
          setChecking(false);
        }
        return;
      }
      try {
        const result = await auth.currentUser.getIdTokenResult(false);
        if (cancelled) return;
        if (result.claims?.superAdmin !== true) {
          setIsSuperAdmin(false);
          return;
        }
        await getAdminWhoami();
        if (cancelled) return;
        setIsSuperAdmin(true);
      } catch (e) {
        if (cancelled) return;
        setIsSuperAdmin(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    }
    setChecking(true);
    void check();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return { isSuperAdmin, loading: authLoading || checking };
}
